import {
  createRpSection,
  rpSectionLibraryEntry,
  type RpLayout,
  type RpPageTheme,
  type RpSection,
  type RpSectionType,
} from './replicated-sections';

/**
 * STARTER TEMPLATES — designed page shapes an admin can begin from.
 *
 * This is where most of the perceived design quality comes from. A section
 * library alone hands a non-designer a blank page and eighteen choices; a
 * template hands them a page that already reads like a landing page, which they
 * then edit. The section order, the alternating band backgrounds, and where the
 * CTAs fall are the actual design work here — the copy is deliberately
 * placeholder, because it must be replaced.
 *
 * A template is a list of sections WITHOUT ids; `applyTemplate` mints them.
 * Unknown types are skipped rather than throwing, so a template stays valid
 * across library changes — that forward-compat is what keeps this file from
 * rotting the first time a section type is renamed.
 */

/** A section in a template: everything but the id, which is minted on apply. */
export type RpTemplateSeed = Omit<RpSection, 'id'>;

export type RpPageTemplate = {
  id: string;
  label: string;
  description: string;
  /** Page-level fields this template suggests when creating a page from it. */
  page?: { ctaLabel?: string; ctaAuto?: boolean };
  theme?: RpPageTheme;
  sections: RpTemplateSeed[];
};

/** Shorthand for a template row. */
function s<T extends RpSectionType>(
  type: T,
  config: Extract<RpSection, { type: T }>['config'],
  style?: RpSection['style'],
): RpTemplateSeed {
  return { type, enabled: true, config, style } as RpTemplateSeed;
}

const SIGNUP = (label: string) => ({
  label,
  link: { kind: 'signup' as const },
  variant: 'primary' as const,
  size: 'lg' as const,
});

export const PAGE_TEMPLATES: RpPageTemplate[] = [
  {
    id: 'blank',
    label: 'Blank',
    description: 'A heading, a paragraph and a call to action. Build up from there.',
    sections: [
      s(
        'heading',
        { text: 'Your headline', level: 1 },
        { width: 'normal', padY: 'lg', align: 'center' },
      ),
      s(
        'richText',
        { html: '<p>Say what this page is about.</p>' },
        { width: 'normal', padY: 'sm' },
      ),
      s(
        'cta',
        { heading: 'Ready to start?', primaryCta: SIGNUP('Sign up') },
        { bg: 'accent', width: 'normal', padY: 'lg', align: 'center' },
      ),
    ],
  },
  {
    id: 'opportunity',
    label: 'Opportunity overview',
    description:
      'The classic recruiting page: the offer, proof, how it works, packages, objections.',
    sections: [
      s(
        'hero',
        {
          variant: 'imageBg',
          eyebrow: 'The opportunity',
          heading: 'Build an income you own',
          subheading: 'Join a team that trains you, supports you, and pays you weekly.',
          primaryCta: SIGNUP('Get started'),
          minHeight: 'lg',
        },
        { bg: 'dark', width: 'wide', padY: 'xl', align: 'center', overlay: 60 },
      ),
      s(
        'stats',
        {
          items: [
            { value: '10,000+', label: 'Members nationwide' },
            { value: '₱1.2B', label: 'Paid out to date' },
            { value: '12 yrs', label: 'In business' },
          ],
        },
        { bg: 'tint', width: 'wide', padY: 'md', align: 'center' },
      ),
      s(
        'steps',
        {
          heading: 'How it works',
          items: [
            { title: 'Join', body: 'Pick a package and register in minutes.' },
            { title: 'Learn', body: 'Get the training and tools you need.' },
            { title: 'Earn', body: 'Share what you love and get paid.' },
          ],
          layout: 'row',
        },
        { bg: 'page', width: 'wide', padY: 'lg', align: 'center' },
      ),
      s(
        'featureGrid',
        { heading: 'Why join us', cols: 3, items: [], cardStyle: 'card' },
        { bg: 'muted', width: 'wide', padY: 'lg', align: 'center' },
      ),
      s(
        'testimonials',
        { heading: 'What our members say', cols: 3, items: [] },
        { bg: 'page', width: 'wide', padY: 'lg', align: 'center' },
      ),
      s(
        'packages',
        { source: 'campaignKits', heading: 'Choose your package', cols: 3, showPrice: true },
        { bg: 'muted', width: 'wide', padY: 'lg', align: 'center' },
      ),
      s(
        'faq',
        { heading: 'Questions people ask', items: [] },
        { bg: 'page', width: 'normal', padY: 'lg' },
      ),
      s(
        'cta',
        {
          heading: 'Start today',
          subheading: 'It takes five minutes to register.',
          primaryCta: SIGNUP('Sign up now'),
        },
        { bg: 'accent', width: 'normal', padY: 'lg', align: 'center' },
      ),
    ],
  },
  {
    id: 'product-launch',
    label: 'Product launch',
    description: 'Lead with the product: the look, the detail, the proof, then the offer.',
    sections: [
      s(
        'hero',
        {
          variant: 'split',
          eyebrow: 'New',
          heading: 'Meet the product everyone is talking about',
          subheading: 'One sentence on what it does and who it is for.',
          media: { kind: 'image', imageKey: '' },
          primaryCta: SIGNUP('Order now'),
          minHeight: 'md',
        },
        { bg: 'page', width: 'wide', padY: 'xl' },
      ),
      s(
        'mediaText',
        {
          variant: 'mediaLeft',
          media: { kind: 'image', imageKey: '' },
          heading: 'Why it works',
          body: 'Explain the single most convincing thing about it.',
        },
        { bg: 'muted', width: 'wide', padY: 'lg' },
      ),
      s(
        'gallery',
        { heading: 'Take a closer look', cols: 3, items: [] },
        { bg: 'page', width: 'wide', padY: 'lg' },
      ),
      s(
        'featureGrid',
        { heading: "What's inside", cols: 3, items: [], cardStyle: 'bordered' },
        { bg: 'muted', width: 'wide', padY: 'lg', align: 'center' },
      ),
      s(
        'testimonials',
        { heading: 'Real results', cols: 3, items: [] },
        { bg: 'page', width: 'wide', padY: 'lg', align: 'center' },
      ),
      s(
        'packages',
        { source: 'campaignKits', heading: 'Pick your bundle', cols: 3, showPrice: true },
        { bg: 'muted', width: 'wide', padY: 'lg', align: 'center' },
      ),
      s(
        'countdown',
        { label: 'Launch pricing ends in', hours: 48, variant: 'blocks' },
        { bg: 'dark', width: 'normal', padY: 'md', align: 'center' },
      ),
      s(
        'cta',
        { heading: 'Get yours before the price goes up', primaryCta: SIGNUP('Order now') },
        { bg: 'accent', width: 'normal', padY: 'lg', align: 'center' },
      ),
    ],
  },
  {
    id: 'webinar',
    label: 'Webinar / event',
    description: 'Get people to show up: what it is, when, why it matters, and register.',
    page: { ctaLabel: 'Save my seat' },
    sections: [
      s(
        'hero',
        {
          variant: 'plain',
          eyebrow: 'Free online event',
          heading: 'How to start earning in 30 days',
          subheading: 'A live walkthrough with our top leaders.',
          primaryCta: SIGNUP('Save my seat'),
          minHeight: 'md',
        },
        { bg: 'dark', width: 'wide', padY: 'xl', align: 'center' },
      ),
      s(
        'countdown',
        { label: 'Registration closes in', hours: 72, variant: 'blocks' },
        { bg: 'tint', width: 'normal', padY: 'md', align: 'center' },
      ),
      s('video', { url: '', aspect: '16:9' }, { bg: 'page', width: 'normal', padY: 'lg' }),
      s(
        'steps',
        {
          heading: "What you'll learn",
          items: [
            { title: 'The plan', body: 'How the compensation actually works.' },
            { title: 'The tools', body: 'What you get on day one.' },
            { title: 'The first 30 days', body: 'Exactly what to do.' },
          ],
          layout: 'row',
        },
        { bg: 'muted', width: 'wide', padY: 'lg', align: 'center' },
      ),
      s(
        'faq',
        { heading: 'Before you register', items: [] },
        { bg: 'page', width: 'normal', padY: 'lg' },
      ),
      s(
        'cta',
        { heading: 'Seats are limited', primaryCta: SIGNUP('Save my seat') },
        { bg: 'accent', width: 'normal', padY: 'lg', align: 'center' },
      ),
    ],
  },
  {
    id: 'coming-soon',
    label: 'Coming soon',
    description: 'A single-screen teaser with a countdown. Good for a launch date.',
    sections: [
      s(
        'hero',
        {
          variant: 'imageBg',
          eyebrow: 'Coming soon',
          heading: 'Something big is on the way',
          subheading: 'Be the first to know when we open.',
          primaryCta: SIGNUP('Notify me'),
          minHeight: 'lg',
        },
        { bg: 'dark', width: 'full', padY: 'xl', align: 'center', overlay: 60 },
      ),
      s(
        'countdown',
        { label: 'Doors open in', hours: 168, variant: 'blocks' },
        { bg: 'page', width: 'normal', padY: 'lg', align: 'center' },
      ),
      s(
        'cta',
        { heading: 'Get on the list', primaryCta: SIGNUP('Notify me') },
        { bg: 'accent', width: 'normal', padY: 'lg', align: 'center' },
      ),
    ],
  },
];

export function rpTemplate(id: string): RpPageTemplate | undefined {
  return PAGE_TEMPLATES.find((t) => t.id === id);
}

/**
 * Expand a template into a concrete layout with freshly minted ids.
 *
 * Skips any section type not in the library. That matters because a template is
 * static data written once and read forever: if a type is ever removed, the
 * template must degrade to "one section shorter", never to a crash on a page
 * the admin is trying to create.
 */
export function applyTemplate(
  template: RpPageTemplate,
  existingIds: readonly string[] = [],
): RpLayout {
  const ids = [...existingIds];
  const sections: RpSection[] = [];
  for (const seed of template.sections) {
    if (!rpSectionLibraryEntry(seed.type)) continue;
    const { id } = createRpSection(seed.type, ids);
    ids.push(id);
    sections.push({ ...seed, id } as RpSection);
  }
  return { version: 1, sections, ...(template.theme ? { theme: template.theme } : {}) };
}
